import { LoanRightsTypes } from '@/shared/constants';
import { defineStore } from 'pinia';

interface QueueInfo {
  people: number | null;
  hours: number | null;
}

interface RouteJumpInfo {
  nextStep: string;
  needAgreement: boolean;
  fundProvided?: number;
  fundName?: string;
  agencyOperationH5?: boolean;
  applyId?: string | number;
}

export interface LoanState {
  amount: string;
  term: string | null;
  selectedNewRightsInterestsType: LoanRightsTypes | null;
  bankcard: Record<string, any> | null;
  loanUsage: string | null;
  reloanPath: string;
  queueInfo: QueueInfo;
  routeJumpInfo: RouteJumpInfo;
  productParams: Record<string, any> | null;
}

type RecordPayload = Partial<
  Pick<
    LoanState,
    | 'amount'
    | 'term'
    | 'reloanPath'
    | 'bankcard'
    | 'loanUsage'
    | 'selectedNewRightsInterestsType'
    | 'productParams'
  >
>;

export const useLoanStore = defineStore('loan', {
  state: (): LoanState => ({
    amount: '',
    term: null,
    bankcard: null,
    loanUsage: null,
    reloanPath: '/loan', // 重新借款路由地址
    // 排队信息
    queueInfo: {
      people: null,
      hours: null,
    },
    // 路由跳转流程信息
    routeJumpInfo: {
      nextStep: '',
      needAgreement: false,
      agencyOperationH5: false,
    },
    selectedNewRightsInterestsType: null,
    productParams: null,
  }),
  getters: {
    record(state) {
      return {
        amount: state.amount,
        term: state.term,
        reloanPath: state.reloanPath,
        bankcard: state.bankcard,
        loanUsage: state.loanUsage,
        selectedNewRightsInterestsType: state.selectedNewRightsInterestsType,
        productParams: state.productParams,
      };
    },
    hasQueueInfo(state) {
      const { people, hours } = state.queueInfo ?? {};
      return people != null && hours != null;
    },
  },
  actions: {
    saveRecord(payload: RecordPayload) {
      this.amount = payload.amount ?? this.amount;
      this.term = payload.term ?? this.term;
      this.reloanPath = payload.reloanPath ?? '/loan';
      this.bankcard = payload.bankcard ?? this.bankcard;
      this.loanUsage = payload.loanUsage ?? this.loanUsage;
      this.selectedNewRightsInterestsType =
        payload.selectedNewRightsInterestsType ?? this.selectedNewRightsInterestsType;
    },
    saveProductParams(payload: RecordPayload) {
      this.productParams = payload;
    },
    clearProductParams() {
      this.productParams = null;
    },
    clearRecord() {
      this.amount = '';
      this.term = null;
      this.reloanPath = '/loan';
      this.bankcard = null;
      this.loanUsage = null;
      this.selectedNewRightsInterestsType = null;
    },
    clearBankcard() {
      this.bankcard = null;
    },
    saveQueueInfo(payload: QueueInfo) {
      this.queueInfo = payload;
    },
    saveRouteJumpInfo(payload: RouteJumpInfo) {
      this.routeJumpInfo = payload;
    },
  },
  persist: true,
});
